import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { api } from '../utils/api';
import Navbar from '../components/Navbar';
import {
  GearSix,
  Radio,
  ChatCircle,
  Users,
  Plus,
  Trash,
  Pencil,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const adminTexts = {
  de: {
    title: 'Admin-Bereich',
    subtitle: 'Verwalte Sender, Kommentare und Nutzer',
    totalStations: 'Sender gesamt',
    totalComments: 'Kommentare gesamt',
    totalUsers: 'Nutzer gesamt',
    stations: 'Sender',
    comments: 'Kommentare',
    users: 'Nutzer',
    addStation: 'Sender hinzufügen',
    createNewStation: 'Neuen Sender erstellen',
    createDescription: 'Füge einen neuen Radiosender zur Plattform hinzu',
    stationName: 'Sendername',
    description: 'Beschreibung',
    country: 'Land',
    city: 'Stadt',
    genre: 'Genre',
    language: 'Sprache',
    streamUrl: 'Stream-URL',
    websiteOptional: 'Website (optional)',
    logoOptional: 'Logo-URL (optional)',
    createStation: 'Sender erstellen',
    noComments: 'Keine Kommentare zur Moderation',
    stationLabel: 'Sender',
    confirmDeleteStation: 'Möchtest du diesen Sender wirklich löschen?',
    confirmDeleteComment: 'Möchtest du diesen Kommentar wirklich löschen?',
  },

  en: {
    title: 'Admin Dashboard',
    subtitle: 'Manage stations, comments, and users',
    totalStations: 'Total Stations',
    totalComments: 'Total Comments',
    totalUsers: 'Total Users',
    stations: 'Stations',
    comments: 'Comments',
    users: 'Users',
    addStation: 'Add Station',
    createNewStation: 'Create New Station',
    createDescription: 'Add a new radio station to the platform',
    stationName: 'Station Name',
    description: 'Description',
    country: 'Country',
    city: 'City',
    genre: 'Genre',
    language: 'Language',
    streamUrl: 'Stream URL',
    websiteOptional: 'Website (optional)',
    logoOptional: 'Logo URL (optional)',
    createStation: 'Create Station',
    noComments: 'No comments to moderate',
    stationLabel: 'Station',
    confirmDeleteStation: 'Are you sure you want to delete this station?',
    confirmDeleteComment: 'Are you sure you want to delete this comment?',
  },

  no: {
    title: 'Adminpanel',
    subtitle: 'Administrer stasjoner, kommentarer og brukere',
    totalStations: 'Totalt antall stasjoner',
    totalComments: 'Totalt antall kommentarer',
    totalUsers: 'Totalt antall brukere',
    stations: 'Stasjoner',
    comments: 'Kommentarer',
    users: 'Brukere',
    addStation: 'Legg til stasjon',
    createNewStation: 'Opprett ny stasjon',
    createDescription: 'Legg til en ny radiostasjon på plattformen',
    stationName: 'Stasjonsnavn',
    description: 'Beskrivelse',
    country: 'Land',
    city: 'By',
    genre: 'Sjanger',
    language: 'Språk',
    streamUrl: 'Stream-URL',
    websiteOptional: 'Nettside (valgfritt)',
    logoOptional: 'Logo-URL (valgfritt)',
    createStation: 'Opprett stasjon',
    noComments: 'Ingen kommentarer å moderere',
    stationLabel: 'Stasjon',
    confirmDeleteStation: 'Er du sikker på at du vil slette denne stasjonen?',
    confirmDeleteComment: 'Er du sikker på at du vil slette denne kommentaren?',
  },

  ko: {
    title: '관리자 대시보드',
    subtitle: '방송국, 댓글 및 사용자를 관리하세요',
    totalStations: '전체 방송국',
    totalComments: '전체 댓글',
    totalUsers: '전체 사용자',
    stations: '방송국',
    comments: '댓글',
    users: '사용자',
    addStation: '방송국 추가',
    createNewStation: '새 방송국 만들기',
    createDescription: '플랫폼에 새 라디오 방송국을 추가합니다',
    stationName: '방송국 이름',
    description: '설명',
    country: '국가',
    city: '도시',
    genre: '장르',
    language: '언어',
    streamUrl: '스트림 URL',
    websiteOptional: '웹사이트 (선택)',
    logoOptional: '로고 URL (선택)',
    createStation: '방송국 만들기',
    noComments: '검토할 댓글이 없습니다',
    stationLabel: '방송국',
    confirmDeleteStation: '이 방송국을 정말 삭제하시겠습니까?',
    confirmDeleteComment: '이 댓글을 정말 삭제하시겠습니까?',
  },

  fi: {
    title: 'Ylläpitopaneeli',
    subtitle: 'Hallitse asemia, kommentteja ja käyttäjiä',
    totalStations: 'Asemia yhteensä',
    totalComments: 'Kommentteja yhteensä',
    totalUsers: 'Käyttäjiä yhteensä',
    stations: 'Asemat',
    comments: 'Kommentit',
    users: 'Käyttäjät',
    addStation: 'Lisää asema',
    createNewStation: 'Luo uusi asema',
    createDescription: 'Lisää uusi radioasema alustalle',
    stationName: 'Aseman nimi',
    description: 'Kuvaus',
    country: 'Maa',
    city: 'Kaupunki',
    genre: 'Genre',
    language: 'Kieli',
    streamUrl: 'Stream-URL',
    websiteOptional: 'Verkkosivusto (valinnainen)',
    logoOptional: 'Logo-URL (valinnainen)',
    createStation: 'Luo asema',
    noComments: 'Ei moderoitavia kommentteja',
    stationLabel: 'Asema',
    confirmDeleteStation: 'Haluatko varmasti poistaa tämän aseman?',
    confirmDeleteComment: 'Haluatko varmasti poistaa tämän kommentin?',
  },
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const text = adminTexts[language] || adminTexts.en;

  const [stations, setStations] = useState([]);
  const [comments, setComments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [newStation, setNewStation] = useState({
    name: '',
    description: '',
    country: '',
    city: '',
    genre: '',
    language: '',
    stream_url: '',
    website: '',
    logo_url: '',
  });

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/discover');
      return;
    }

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const loadData = async () => {
    try {
      const [stationsRes, commentsRes, usersRes] = await Promise.all([
        api.adminGetStations(),
        api.adminGetComments(),
        api.adminGetUsers(),
      ]);

      setStations(stationsRes.data);
      setComments(commentsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to load admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStation = async (e) => {
    e.preventDefault();

    try {
      await api.createStation(newStation);

      setIsDialogOpen(false);
      setNewStation({
        name: '',
        description: '',
        country: '',
        city: '',
        genre: '',
        language: '',
        stream_url: '',
        website: '',
        logo_url: '',
      });

      loadData();
    } catch (error) {
      console.error('Failed to create station:', error);
    }
  };

  const handleDeleteStation = async (stationId) => {
    if (!window.confirm(text.confirmDeleteStation)) return;

    try {
      await api.deleteStation(stationId);
      loadData();
    } catch (error) {
      console.error('Failed to delete station:', error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm(text.confirmDeleteComment)) return;

    try {
      await api.deleteComment(commentId);
      loadData();
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
  };

  const updateNewStation = (key, value) => {
    setNewStation((current) => ({
      ...current,
      [key]: value,
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pb-24 md:pb-0">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <GearSix size={40} weight="duotone" className="text-cyan-400" />

            <h1
              className="text-4xl sm:text-5xl tracking-tighter font-black text-white"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {text.title}
            </h1>
          </div>

          <p className="text-base text-gray-300">{text.subtitle}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Radio size={24} weight="duotone" className="text-cyan-400" />
              <span className="text-sm font-semibold text-gray-400">
                {text.totalStations}
              </span>
            </div>
            <p className="text-3xl font-black text-white">{stations.length}</p>
          </div>

          <div className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <ChatCircle size={24} weight="duotone" className="text-cyan-400" />
              <span className="text-sm font-semibold text-gray-400">
                {text.totalComments}
              </span>
            </div>
            <p className="text-3xl font-black text-white">{comments.length}</p>
          </div>

          <div className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users size={24} weight="duotone" className="text-cyan-400" />
              <span className="text-sm font-semibold text-gray-400">
                {text.totalUsers}
              </span>
            </div>
            <p className="text-3xl font-black text-white">{users.length}</p>
          </div>
        </div>

        {/* Management Tabs */}
        <div className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-8">
          <Tabs defaultValue="stations" className="w-full">
            <TabsList className="bg-[#0A0E17] border border-white/10 mb-6">
              <TabsTrigger
                data-testid="admin-tab-stations"
                value="stations"
                className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black"
              >
                {text.stations}
              </TabsTrigger>

              <TabsTrigger
                data-testid="admin-tab-comments"
                value="comments"
                className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black"
              >
                {text.comments}
              </TabsTrigger>

              <TabsTrigger
                data-testid="admin-tab-users"
                value="users"
                className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black"
              >
                {text.users}
              </TabsTrigger>
            </TabsList>

            {/* Stations Tab */}
            <TabsContent value="stations">
              <div className="mb-6">
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid="add-station-btn"
                      className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-full flex items-center gap-2"
                    >
                      <Plus size={20} weight="bold" />
                      {text.addStation}
                    </Button>
                  </DialogTrigger>

                  <DialogContent className="bg-[#0A0E17] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-bold">
                        {text.createNewStation}
                      </DialogTitle>

                      <DialogDescription className="text-gray-400">
                        {text.createDescription}
                      </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleCreateStation} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="name" className="text-white">
                          {text.stationName}
                        </Label>
                        <Input
                          data-testid="station-name-input"
                          id="name"
                          value={newStation.name}
                          onChange={(e) => updateNewStation('name', e.target.value)}
                          className="bg-[#121826] border-white/10 text-white"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="description" className="text-white">
                          {text.description}
                        </Label>
                        <Textarea
                          id="description"
                          value={newStation.description}
                          onChange={(e) =>
                            updateNewStation('description', e.target.value)
                          }
                          className="bg-[#121826] border-white/10 text-white"
                          required
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="country" className="text-white">
                            {text.country}
                          </Label>
                          <Input
                            id="country"
                            value={newStation.country}
                            onChange={(e) =>
                              updateNewStation('country', e.target.value)
                            }
                            className="bg-[#121826] border-white/10 text-white"
                            required
                          />
                        </div>

                        <div>
                          <Label htmlFor="city" className="text-white">
                            {text.city}
                          </Label>
                          <Input
                            id="city"
                            value={newStation.city}
                            onChange={(e) => updateNewStation('city', e.target.value)}
                            className="bg-[#121826] border-white/10 text-white"
                            required
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="genre" className="text-white">
                            {text.genre}
                          </Label>
                          <Input
                            id="genre"
                            value={newStation.genre}
                            onChange={(e) =>
                              updateNewStation('genre', e.target.value)
                            }
                            className="bg-[#121826] border-white/10 text-white"
                            required
                          />
                        </div>

                        <div>
                          <Label htmlFor="language" className="text-white">
                            {text.language}
                          </Label>
                          <Input
                            id="language"
                            value={newStation.language}
                            onChange={(e) =>
                              updateNewStation('language', e.target.value)
                            }
                            className="bg-[#121826] border-white/10 text-white"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="stream_url" className="text-white">
                          {text.streamUrl}
                        </Label>
                        <Input
                          id="stream_url"
                          value={newStation.stream_url}
                          onChange={(e) =>
                            updateNewStation('stream_url', e.target.value)
                          }
                          className="bg-[#121826] border-white/10 text-white"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="website" className="text-white">
                          {text.websiteOptional}
                        </Label>
                        <Input
                          id="website"
                          value={newStation.website}
                          onChange={(e) =>
                            updateNewStation('website', e.target.value)
                          }
                          className="bg-[#121826] border-white/10 text-white"
                        />
                      </div>

                      <div>
                        <Label htmlFor="logo_url" className="text-white">
                          {text.logoOptional}
                        </Label>
                        <Input
                          id="logo_url"
                          value={newStation.logo_url}
                          onChange={(e) =>
                            updateNewStation('logo_url', e.target.value)
                          }
                          className="bg-[#121826] border-white/10 text-white"
                        />
                      </div>

                      <Button
                        data-testid="create-station-btn"
                        type="submit"
                        className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-full"
                      >
                        {text.createStation}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="space-y-4">
                {stations.map((station) => (
                  <div
                    key={station.station_id}
                    data-testid={`admin-station-${station.station_id}`}
                    className="bg-[#0A0E17] border border-white/5 rounded-xl p-6 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <img
                        src={
                          station.logo_url ||
                          'https://images.unsplash.com/photo-1773053525998-8cb667020fa7?w=100'
                        }
                        alt={station.name}
                        onError={(e) => {
                          e.target.src =
                            'https://images.unsplash.com/photo-1773053525998-8cb667020fa7?w=100';
                        }}
                        className="w-16 h-16 rounded-lg object-cover"
                      />

                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          {station.name}
                        </h3>
                        <p className="text-sm text-gray-400">
                          {station.country} • {station.genre}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={() => navigate(`/station/${station.station_id}`)}
                        variant="outline"
                        size="sm"
                        className="rounded-full border-white/10 bg-white/5 text-white hover:bg-white/10"
                      >
                        <Pencil size={16} />
                      </Button>

                      <Button
                        data-testid={`delete-station-${station.station_id}`}
                        onClick={() => handleDeleteStation(station.station_id)}
                        variant="outline"
                        size="sm"
                        className="rounded-full border-red-500/50 bg-red-500/10 text-red-400 hover:bg-red-500/20"
                      >
                        <Trash size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Comments Tab */}
            <TabsContent value="comments">
              <div className="space-y-4">
                {comments.length === 0 ? (
                  <p className="text-center text-gray-400 py-8">
                    {text.noComments}
                  </p>
                ) : (
                  comments.map((comment) => (
                    <div
                      key={comment.comment_id}
                      data-testid={`admin-comment-${comment.comment_id}`}
                      className="bg-[#0A0E17] border border-white/5 rounded-xl p-6"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <span className="font-semibold text-white">
                            {comment.user_id}
                          </span>
                          <p className="text-sm text-gray-500">
                            {text.stationLabel}: {comment.station_id}
                          </p>
                        </div>

                        <Button
                          data-testid={`delete-comment-btn-${comment.comment_id}`}
                          onClick={() => handleDeleteComment(comment.comment_id)}
                          variant="outline"
                          size="sm"
                          className="rounded-full border-red-500/50 bg-red-500/10 text-red-400 hover:bg-red-500/20"
                        >
                          <Trash size={16} />
                        </Button>
                      </div>

                      <p className="text-gray-300">{comment.content}</p>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users">
              <div className="space-y-4">
                {users.map((u) => (
                  <div
                    key={u.user_id || u.email}
                    data-testid={`admin-user-${u.username}`}
                    className="bg-[#0A0E17] border border-white/5 rounded-xl p-6 flex items-center justify-between"
                  >
                    <div>
                      <h3 className="text-lg font-semibold text-white">
                        {u.name}
                      </h3>
                      <p className="text-sm text-gray-400">
                        @{u.username} • {u.email}
                      </p>
                      <span
                        className={`text-xs font-bold px-2 py-1 rounded mt-2 inline-block ${u.role === 'admin'
                            ? 'bg-cyan-500/20 text-cyan-400'
                            : 'bg-white/10 text-gray-300'
                          }`}
                      >
                        {u.role}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;